import {
  createContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
  useMemo,
  ReactNode,
  FC,
  useContext,
  ComponentType,
  ComponentProps,
} from 'react'
import { useUser } from 'hooks/UseUser'
import { Settings } from 'luxon'
import { useCurrentUserOptions as useCurrentUserOptionsApi } from 'api/UserOptions'
import { useFlagsForSite } from 'api/Sites'
import { useShopSite } from 'api/ShopSite'
import type { ShopSite } from '@trybeapp/sdk'
import type { SetOptional } from 'type-fest'
import * as Sentry from '@sentry/react'

interface SiteConfig {
  activeSiteId: string
  setActiveSiteId: Dispatch<SetStateAction<string>>
  site: ShopSite
  siteId: string
}

export const SiteConfigContext = createContext<SiteConfig>({} as SiteConfig)

export const useSiteConfig = () => useContext(SiteConfigContext)

interface SiteConfigProviderProps {
  children: ReactNode
}

export const SiteConfigProvider: FC<SiteConfigProviderProps> = ({ children }) => {
  const { default_site_id: siteId, site_ids: validSiteIds = [] } = useUser()
  const [activeSiteId, setActiveSiteId] = useState(() => {
    const siteInStorage = sessionStorage.getItem('activeSiteId')
    const defaultSiteId =
      siteInStorage && validSiteIds.includes(siteInStorage) ? siteInStorage : siteId

    return defaultSiteId || siteId || ''
  })
  const siteConfig = useProvideSiteConfig(activeSiteId)

  const context = useMemo<SetOptional<SiteConfig, 'site'>>(
    () => ({ ...siteConfig, activeSiteId, setActiveSiteId }),
    [activeSiteId, siteConfig]
  )

  // Load user options so we have them in the query cache for later
  useCurrentUserOptions()

  useEffect(() => {
    sessionStorage.setItem('activeSiteId', activeSiteId)
  }, [activeSiteId])

  useEffect(() => {
    if (!activeSiteId && siteId) {
      setActiveSiteId(siteId)
    }
  }, [activeSiteId, siteId])

  useEffect(() => {
    if (!context.site) return

    Sentry.setTags({
      trybe_site_id: context.site.id,
      trybe_site_name: context.site.name,
    })
  }, [context.site])

  if (!context.site) return null

  return (
    <SiteConfigContext.Provider value={context as SiteConfig}>
      {children}
    </SiteConfigContext.Provider>
  )
}

export const useProvideSiteConfig = (siteId: string) => {
  const { data: { data: site } = {} } = useShopSite(siteId)

  useEffect(() => {
    Settings.defaultZone = site?.timezone ?? 'Europe/London'
  }, [site])

  return useMemo(() => ({ siteId, site }) as const, [site, siteId])
}

export const useCurrentSiteId = () => {
  const { activeSiteId } = useSiteConfig()

  return activeSiteId
}

export const useCurrentSite = () => {
  const { site } = useSiteConfig()

  return site
}

export const useActiveFeatureFlags = () => {
  const currentSiteId = useCurrentSiteId()
  const { data: { data: flags } = {} } = useFlagsForSite(currentSiteId, {
    cacheTime: Infinity,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  return useMemo(() => flags ?? [], [flags])
}

export const useCurrentUserOptions = () => {
  const { data: { data: userOptions = {} } = {} } = useCurrentUserOptionsApi()

  return userOptions
}

export function withSiteConfig<C extends ComponentType<any>>(Component: C): FC<ComponentProps<C>> {
  return (props) => {
    return (
      <SiteConfigProvider>
        <Component {...props} />
      </SiteConfigProvider>
    )
  }
}
